import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image as ImageIcon, Paperclip, Smile, Globe, FileText } from 'lucide-react';
import { useApp } from './store';
interface ComposerModalProps {
  open: boolean;
  onClose: () => void;
}
const audiences = ['Whole company', 'Phoenix Branch', 'Sales', 'Operations', 'My team'];
const emojis = ['😀', '😄', '😊', '😍', '🥳', '😎', '🤝', '👏', '🙌', '👍', '🔥', '🎉', '🚀', '💪', '💡', '⭐', '✅', '❤️', '🏆', '🏡'];
export function ComposerModal({
  open,
  onClose
}: ComposerModalProps) {
  const {
    user,
    addPost
  } = useApp();
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState(audiences[0]);
  const [image, setImage] = useState('');
  const [attachment, setAttachment] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [posted, setPosted] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const imageRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setBody('');
      setImage('');
      setAttachment('');
      setEmojiOpen(false);
      setPosted(false);
    }
  }, [open]);
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setImage(String(reader.result));
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Images attach as the post image; other files attach as a named reference.
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setImage(String(reader.result));
      reader.readAsDataURL(file);
    } else {
      setAttachment(file.name);
    }
    e.target.value = '';
  };
  const insertEmoji = (emoji: string) => {
    const el = textRef.current;
    if (el) {
      const start = el.selectionStart ?? body.length;
      const end = el.selectionEnd ?? body.length;
      const next = body.slice(0, start) + emoji + body.slice(end);
      setBody(next);
      // Restore caret after the inserted emoji
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setBody(b => b + emoji);
    }
    setEmojiOpen(false);
  };
  const submit = () => {
    const hasContent = body.trim() || image || attachment;
    if (!hasContent) return;
    const finalBody = attachment ? `${body.trim()}${body.trim() ? '\n\n' : ''}📎 Attached: ${attachment}` : body.trim();
    addPost({
      body: finalBody,
      audience,
      image: image || undefined
    });
    setPosted(true);
    setTimeout(onClose, 1100);
  };
  return <AnimatePresence data-id="element-71">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:pt-24 bg-navy-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-72">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.2
      }} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Create a post" data-id="element-73">
            <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100" data-id="element-74">
              <h2 className="font-serif font-bold text-lg text-navy-900" data-id="element-75">
                Create Post
              </h2>
              <button onClick={onClose} className="p-1.5 text-navy-400 hover:bg-navy-50 rounded-full" aria-label="Close" data-id="element-76">
                <X className="w-5 h-5" data-id="element-77" />
              </button>
            </div>

            {posted ? <div className="p-10 text-center" data-id="element-78">
                <div className="w-14 h-14 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-3 text-2xl" data-id="element-79">
                  ✓
                </div>
                <p className="font-serif font-bold text-navy-900 text-lg" data-id="element-80">
                  Posted to {audience}!
                </p>
                <p className="text-sm text-navy-500 mt-1" data-id="element-81">
                  Your update is now live in the feed.
                </p>
              </div> : <>
                <div className="p-5" data-id="element-82">
                  <div className="flex items-center gap-3 mb-4" data-id="element-83">
                    <img src={user.avatar} alt="" className="w-11 h-11 rounded-full object-cover" data-id="element-84" />
                    <div data-id="element-85">
                      <p className="text-sm font-bold text-navy-900" data-id="element-86">
                        {user.name}
                      </p>
                      <span className="inline-flex items-center gap-1.5 mt-0.5 text-xs font-semibold text-navy-500" data-id="element-87">
                        <Globe className="w-3.5 h-3.5" data-id="element-88" />
                        <select value={audience} onChange={e => setAudience(e.target.value)} className="bg-navy-50 rounded-md px-2 py-1 text-navy-700 focus:outline-none focus:ring-2 focus:ring-gold-400/40" data-id="element-89">
                          {audiences.map(a => <option key={a} data-id="element-90">{a}</option>)}
                        </select>
                      </span>
                    </div>
                  </div>

                  <textarea ref={textRef} autoFocus value={body} onChange={e => setBody(e.target.value)} rows={5} placeholder="Share an update, a win, or a question with the team…" className="w-full text-navy-900 placeholder:text-navy-400 text-base resize-none focus:outline-none" data-id="element-91" />

                  {/* Image preview */}
                  {image && <div className="relative mt-3 rounded-xl overflow-hidden border border-navy-100" data-id="element-92">
                      <img src={image} alt="Attached" className="w-full max-h-64 object-cover" data-id="element-93" />
                      <button onClick={() => setImage('')} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-navy-900/70 text-white flex items-center justify-center hover:bg-navy-900" aria-label="Remove image" data-id="element-94">
                        <X className="w-4 h-4" data-id="element-95" />
                      </button>
                    </div>}

                  {/* File attachment chip */}
                  {attachment && <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-navy-50 border border-navy-100" data-id="element-96">
                      <FileText className="w-4 h-4 text-navy-500 shrink-0" data-id="element-97" />
                      <span className="text-sm text-navy-700 truncate flex-1" data-id="element-98">
                        {attachment}
                      </span>
                      <button onClick={() => setAttachment('')} className="p-1 text-navy-400 hover:text-navy-700" aria-label="Remove attachment" data-id="element-99">
                        <X className="w-4 h-4" data-id="element-100" />
                      </button>
                    </div>}
                </div>

                <div className="relative flex items-center gap-1 px-5 py-3 border-t border-navy-100" data-id="element-101">
                  <button onClick={() => imageRef.current?.click()} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label="Add photo" data-id="element-102">
                    <ImageIcon className="w-5 h-5" data-id="element-103" />
                  </button>
                  <button onClick={() => fileRef.current?.click()} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label="Attach file" data-id="element-104">
                    <Paperclip className="w-5 h-5" data-id="element-105" />
                  </button>
                  <button onClick={() => setEmojiOpen(v => !v)} className={`p-2 rounded-lg ${emojiOpen ? 'bg-gold-100 text-gold-700' : 'text-navy-500 hover:bg-navy-50'}`} aria-label="Add emoji" aria-expanded={emojiOpen} data-id="element-106">
                    <Smile className="w-5 h-5" data-id="element-107" />
                  </button>

                  <button onClick={submit} disabled={!body.trim() && !image && !attachment} className="ml-auto px-6 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wide" data-id="element-108">
                    Post
                  </button>

                  {/* Emoji picker */}
                  <AnimatePresence data-id="element-109">
                    {emojiOpen && <motion.div initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 6
              }} transition={{
                duration: 0.15
              }} className="absolute bottom-full left-4 mb-2 bg-white rounded-xl border border-navy-100 shadow-xl p-2 grid grid-cols-8 gap-1 w-72 z-10" data-id="element-110">
                        {emojis.map(em => <button key={em} onClick={() => insertEmoji(em)} className="w-8 h-8 rounded-lg hover:bg-navy-50 text-xl leading-none flex items-center justify-center" aria-label={`Insert ${em}`} data-id="element-111">
                            {em}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>

                  {/* Hidden inputs */}
                  <input ref={imageRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-id="element-112" />
                  <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" data-id="element-113" />
                </div>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}